import { Button, Checkbox, Flex, Select, TextInput } from '@mantine/core';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './styles.module.css';
import { themeColors } from '@/theme/colors';
import { TFormValuesDlp } from './useDlpForm';
import { UseFormReturnType } from '@mantine/form';
import {
  DlpFileTypesMode,
  DlpOnTimeout,
  IcapServerDto
} from '@/services/api/dlpSettingsApiArea/types';
import { IUsePopUpUI, PopUpUI } from '@components/UI/PopUp';
import { ServerSettings } from './ServerSettings';
import { ServersTable } from './ServersTable';

interface DLPFormProps {
  form: UseFormReturnType<TFormValuesDlp>;
  servers: IcapServerDto[];
  isEditMode?: boolean;
  hasServerChanges?: boolean;
  addServer(server: IcapServerDto): void;
  deleteServer(index: number): void;
  editServer(server: IcapServerDto, index: number): void;
  handleSubmit(): void;
  onCancel?(): void;
}

type EditingServer = {
  server: IcapServerDto;
  index: number;
} | null;

type PopupSetIsOpenRef = ((value: boolean) => void) | null;

const ServerSettingsPopUpTrigger = (props: IUsePopUpUI & { t: (key: string) => string }) => {
  const { setIsOpen, t } = props;

  const handleOpen = () => setIsOpen(true);

  return (
    <Button
      className={classes.addButton}
      size="sm"
      color="#667085"
      variant="outline"
      rightSection={<IconPlus color={themeColors.brandGray[0]} />}
      onClick={handleOpen}
    >
      {t('dlp.form.addServer')}
    </Button>
  );
};

const DLPForm = ({
  form,
  handleSubmit,
  onCancel,
  servers,
  addServer,
  deleteServer,
  editServer,
  isEditMode = false,
  hasServerChanges = false
}: DLPFormProps) => {
  const { t } = useTranslation();
  const [editingServer, setEditingServer] = useState<EditingServer>(null);
  const popupSetIsOpenRef = useRef<PopupSetIsOpenRef>(null);

  const handleEditServer = (server: IcapServerDto, index: number) => {
    setEditingServer({ server, index });
    if (popupSetIsOpenRef.current) {
      popupSetIsOpenRef.current(true);
    }
  };

  const handleServerSuccess = (server: IcapServerDto) => {
    if (editingServer !== null) {
      editServer(server, editingServer.index);
      setEditingServer(null);
    } else {
      addServer(server);
    }
  };

  // Helper to handle nullable number inputs
  const getNumberInputProps = (field: keyof TFormValuesDlp) => {
    const props = form.getInputProps(field);
    return {
      ...props,
      // eslint-disable-next-line react/prop-types
      value: props.value ?? ''
    };
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.container}>
      <div className={classes.formContent}>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.title')}</div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.name')}
              {...form.getInputProps('name')}
            />
            {/*!!Может понадобиться!!*/}
            {/*<Select*/}
            {/*  className={classes.fullWidth}*/}
            {/*  data={['ICAP', 'Антивирус', 'DLP']}*/}
            {/*  label="Тип"*/}
            {/*  value={form.values.type || null}*/}
            {/*  onChange={(value) => form.setFieldValue('type', value || '')}*/}
            {/*  comboboxProps={{ zIndex: 10000 }}*/}
            {/*/>*/}
          </div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.host')}
              {...form.getInputProps('host')}
            />
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.port')}
              type="number"
              {...form.getInputProps('port')}
            />
          </div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.respmodService')}
              {...form.getInputProps('respmodService')}
            />
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.reqmodService')}
              {...form.getInputProps('reqmodService')}
            />
          </div>
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.additionalServers')}</div>
          <PopUpUI
            mobileSize="100%"
            desktopPosition="bottom-start"
            trigger={(props) => <ServerSettingsPopUpTrigger {...props} t={t} />}
          >
            {({ isOpen, setIsOpen }) => {
              popupSetIsOpenRef.current = setIsOpen;
              return (
                <ServerSettings
                  isOpen={isOpen}
                  setIsOpen={(value) => {
                    if (!value) {
                      setEditingServer(null);
                    }
                    setIsOpen(value);
                  }}
                  server={editingServer?.server}
                  onSuccess={handleServerSuccess}
                />
              );
            }}
          </PopUpUI>
          <ServersTable servers={servers} onEdit={handleEditServer} onDelete={deleteServer} />
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.timeoutsAndAttempts')}</div>
          <div className={classes.timeoutContainer}>
            <TextInput
              className={classes.timeoutItem}
              label={t('dlp.form.connectTimeout')}
              type="number"
              {...form.getInputProps('connectTimeoutMs')}
            />
            <TextInput
              className={classes.timeoutItem}
              label={t('dlp.form.writeTimeout')}
              type="number"
              {...form.getInputProps('writeTimeoutMs')}
            />
            <TextInput
              className={classes.timeoutItem}
              label={t('dlp.form.readTimeout')}
              type="number"
              {...form.getInputProps('readTimeoutMs')}
            />
            <TextInput
              className={classes.timeoutItem}
              label={t('dlp.form.resultTimeout')}
              type="number"
              {...form.getInputProps('resultTimeoutMs')}
            />
            <TextInput
              className={classes.timeoutItem}
              label={t('dlp.form.attempts')}
              type="number"
              {...form.getInputProps('attempts')}
            />
            <TextInput
              className={classes.timeoutItem}
              label={t('dlp.form.backoff')}
              type="number"
              {...form.getInputProps('backoffMs')}
            />
            <TextInput
              className={`${classes.timeoutItem} ${classes.timeoutItemWide}`}
              label={t('dlp.form.maxConnections')}
              type="number"
              {...getNumberInputProps('maxConnections')}
            />
            <Select
              className={`${classes.timeoutItem} ${classes.timeoutItemWide}`}
              rightSection={<IconChevronDown size="1rem" color="#98A2B3" />}
              data={[
                { value: String(DlpOnTimeout.Block), label: t('dlp.form.onTimeoutBlock') },
                { value: String(DlpOnTimeout.Allow), label: t('dlp.form.onTimeoutAllow') },
                { value: String(DlpOnTimeout.Quarantine), label: t('dlp.form.onTimeoutQuarantine') }
              ]}
              label={t('dlp.form.onTimeout')}
              comboboxProps={{ zIndex: 10000 }}
              value={form.values.onTimeout !== undefined ? String(form.values.onTimeout) : null}
              onChange={(value) =>
                form.setFieldValue('onTimeout', value ? Number(value) : DlpOnTimeout.Block)
              }
              error={form.errors.onTimeout}
            />
          </div>
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.scanConditions')}</div>
          <div>
            <Checkbox
              fz="14px"
              mb={12}
              label={t('dlp.form.scanUpload')}
              {...form.getInputProps('scanUpload', { type: 'checkbox' })}
            />
            <Checkbox
              fz="14px"
              mb={12}
              label={t('dlp.form.scanDownload')}
              {...form.getInputProps('scanDownload', { type: 'checkbox' })}
            />
            <Checkbox
              fz="14px"
              mb={12}
              label={t('dlp.form.scanShareInternal')}
              {...form.getInputProps('scanShareInternal', { type: 'checkbox' })}
            />
            {/*<Checkbox*/}
            {/*  fz="14px"*/}
            {/*  mb={12}*/}
            {/*  label={t('dlp.form.scanShareGuest')}*/}
            {/*  {...form.getInputProps('scanShareGuest', { type: 'checkbox' })}*/}
            {/*/>*/}
            <Checkbox
              fz="14px"
              mb={12}
              label={t('dlp.form.scanExternalLink')}
              {...form.getInputProps('scanExternalLink', { type: 'checkbox' })}
            />
          </div>
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.fileTypeFiltering')}</div>
          <div className={classes.filterContainer}>
            <Select
              className={`${classes.timeoutItem} ${classes.filterItemWide}`}
              rightSection={<IconChevronDown size="1rem" color="#98A2B3" />}
              data={[
                { value: String(DlpFileTypesMode.Off), label: t('dlp.form.fileTypeModeOff') },
                {
                  value: String(DlpFileTypesMode.AllowList),
                  label: t('dlp.form.fileTypeModeAllowList')
                },
                {
                  value: String(DlpFileTypesMode.DenyList),
                  label: t('dlp.form.fileTypeModeDenyList')
                }
              ]}
              label={t('dlp.form.fileTypesMode')}
              comboboxProps={{ zIndex: 10000 }}
              value={
                form.values.fileTypesMode !== undefined ? String(form.values.fileTypesMode) : null
              }
              onChange={(value) =>
                form.setFieldValue('fileTypesMode', value ? Number(value) : DlpFileTypesMode.Off)
              }
              error={form.errors.fileTypesMode}
            />
            {form.values.fileTypesMode === DlpFileTypesMode.AllowList && (
              <>
                <TextInput
                  className={classes.filterItem}
                  label={t('dlp.form.allowedExt')}
                  {...form.getInputProps('allowedExt')}
                />
                <TextInput
                  className={classes.filterItem}
                  label={t('dlp.form.allowedMime')}
                  {...form.getInputProps('allowedMime')}
                />
              </>
            )}
            {form.values.fileTypesMode === DlpFileTypesMode.DenyList && (
              <>
                <TextInput
                  className={classes.filterItem}
                  label={t('dlp.form.blockedExt')}
                  {...form.getInputProps('blockedExt')}
                />
                <TextInput
                  className={classes.filterItem}
                  label={t('dlp.form.blockedMime')}
                  {...form.getInputProps('blockedMime')}
                />
              </>
            )}
          </div>
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.restrictionsAndSettings')}</div>
          <div className={classes.restrictions}>
            <TextInput
              className={classes.restrictionsItem}
              label={t('dlp.form.maxFileSizeMb')}
              type="number"
              {...getNumberInputProps('maxFileSizeMb')}
            />
            <TextInput
              className={classes.restrictionsItem}
              label={t('dlp.form.whenDownWaitMs')}
              type="number"
              {...form.getInputProps('whenDownWaitMs')}
            />
            <TextInput
              className={`${classes.restrictionsItem} ${classes.restrictionsItemWide}`}
              label={t('dlp.form.maxMessageSizeKb')}
              type="number"
              {...getNumberInputProps('maxMessageSizeKb')}
            />
          </div>
          <Checkbox
            size="xs"
            fz="14px"
            mb={12}
            label={t('dlp.form.skipOnIcapErrors')}
            {...form.getInputProps('skipOnIcapErrors', { type: 'checkbox' })}
          />
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.cachingAndHashing')}</div>
          <div>
            <TextInput
              label={t('dlp.form.hashTtlMinutes')}
              type="number"
              {...getNumberInputProps('hashTtlMinutes')}
            />
          </div>
          {/*<Checkbox*/}
          {/*  size="xs"*/}
          {/*  fz="14px"*/}
          {/*  mb={12}*/}
          {/*  label={t('dlp.form.useHashCache')}*/}
          {/*  {...form.getInputProps('useHashCache', { type: 'checkbox' })}*/}
          {/*/>*/}
        </Flex>
        <Flex direction="column" gap={24}>
          <div className={classes.title}>{t('dlp.form.headersAndMetadata')}</div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerFileName')}
              {...form.getInputProps('headerFileName')}
            />
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerFileSize')}
              {...form.getInputProps('headerFileSize')}
            />
          </div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerFileId')}
              {...form.getInputProps('headerFileId')}
            />
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerFileHash')}
              {...form.getInputProps('headerFileHash')}
            />
          </div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerRecipientEmail')}
              {...form.getInputProps('headerRecipientEmail')}
            />
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerUserEmail')}
              {...form.getInputProps('headerUserEmail')}
            />
          </div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerRoleId')}
              {...form.getInputProps('headerRoleId')}
            />
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerRoleName')}
              {...form.getInputProps('headerRoleName')}
            />
          </div>
          <div className={classes.flexRow}>
            <TextInput
              className={classes.fullWidth}
              label={t('dlp.form.headerRoleType')}
              {...form.getInputProps('headerRoleType')}
            />
          </div>
        </Flex>
      </div>
      <div className={classes.buttonGroup}>
        <Button
          type="submit"
          fullWidth
          disabled={!form.isValid() || (isEditMode && !form.isDirty() && !hasServerChanges)}
        >
          {isEditMode ? t('dlp.form.saveButton') : t('dlp.form.createButton')}
        </Button>
        <Button variant="light" color="gray" fullWidth onClick={onCancel}>
          {t('dlp.form.cancelButton')}
        </Button>
      </div>
    </form>
  );
};

export default DLPForm;
